import React, { useCallback, useEffect } from 'react';
import PropType from 'prop-types';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import _ from '../../lodash-wrapper';

import { Alert } from '@material-ui/lab';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Pagination from '@material-ui/lab/Pagination';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Box from '@material-ui/core/Box';
import { CourseCard } from '../shared/courseCard';

import { getCourseGroupUrl } from '../../utils';
import { SEARCHSCREEN_PAGE_SIZE } from '../../constants';
import { CourseGroupSearchResult } from '../../models/courseGroup';
import { CourseGroupOrdering } from '../../models/enums';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        flex: 1,
        padding: '20px',
        [theme.breakpoints.down(800)]: {padding: '20px 12px 10px '},
        [theme.breakpoints.down('xs')]: {padding: '20px 8px 10px '},
    },
    heading: {
        fontWeight: 600,
        fontSize: '20px',
        paddingLeft: '58px',
        marginTop: '0px',
        marginBottom: '20px',
        [theme.breakpoints.up('sm')]: {
            fontSize: '20px',
            paddingLeft: '58px',
            marginTop: '5px',
            marginBottom: '20px',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '24px',
            paddingLeft: '20px',
            marginBottom: '15px',
        },
    },
    orderingContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: '20px',
        paddingLeft: '58px',
        [theme.breakpoints.up('md')]: {
            paddingLeft: '20px',
        },
    },
    orderingLabel: {
        marginRight: '12px',
        fontSize: '16px',
        fontWeight: 600,
        color: theme.palette.text.primary,
        [theme.breakpoints.down('xs')]: {
            fontSize: '14px',
            marginRight: '8px',
        },
    },
    orderingButton: {
        fontSize: '16px',
        padding: '8px 16px',
        [theme.breakpoints.down('xs')]: {
            fontSize: '14px',
            padding: '6px 12px',
        },
    },
    paginationContainer: {
        marginTop: '30px',
        display: 'flex',
        flex: 1,
        justifyContent: 'center'
    },
    error: {
        marginBottom: '20px',
    }
}));

export default function CourseGroupSearchResultSection(props) {
    const classes = useStyles(useTheme());
    const { error, searchResult, requestPage, currentPage, ordering, onChangeOrdering, hasQuery } = props;
    const getUrl = useCallback(group => getCourseGroupUrl(group.id), []);
    const onChangePage = useCallback(async (event, page) => {
        await requestPage(page);
    }, [requestPage]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [searchResult])

    return (
        <Container className={classes.root}>
            <Typography variant="h1" component="h1" className={classes.heading}>搜尋學識</Typography>
            
            {/* Sorting Options */}
            <Box className={classes.orderingContainer}>
                <Typography className={classes.orderingLabel}>排序:</Typography>
                <ButtonGroup size="small" color="primary" aria-label="sorting options">
                    <Button
                        className={classes.orderingButton}
                        variant={ordering === CourseGroupOrdering.LATEST ? 'contained' : 'outlined'}
                        onClick={() => onChangeOrdering(CourseGroupOrdering.LATEST)}
                    >
                        {CourseGroupOrdering.LATEST.displayName}
                    </Button>
                    <Button
                        className={classes.orderingButton}
                        variant={ordering === CourseGroupOrdering.RECOMMENDED ? 'contained' : 'outlined'}
                        onClick={() => onChangeOrdering(CourseGroupOrdering.RECOMMENDED)}
                    >
                        {CourseGroupOrdering.RECOMMENDED.displayName}
                    </Button>
                    <Button
                        className={classes.orderingButton}
                        variant={ordering === CourseGroupOrdering.RELEVANCE ? 'contained' : 'outlined'}
                        onClick={() => onChangeOrdering(CourseGroupOrdering.RELEVANCE)}
                        disabled={!hasQuery}
                        title={!hasQuery ? '請先輸入關鍵字才能使用相關度排序' : ''}
                    >
                        {CourseGroupOrdering.RELEVANCE.displayName}
                    </Button>
                </ButtonGroup>
            </Box>

            { error && <Alert className={classes.error} severity='error'>{error.message}</Alert> }
            { searchResult && _.size(searchResult.results) === 0 &&
                <Alert className={classes.error} severity='info'>暫時未有相關課程</Alert>
            }
            {searchResult &&
            <>
                <Grid container spacing={2}>
                    {searchResult.results.map(g =>
                        <CourseCard
                            key={g.id}
                            target={g}
                            urlGenerator={getUrl}
                            md={6}
                            lg={4}
                        />
                    )}
                </Grid>
                <Container className={classes.paginationContainer}>
                    <Pagination
                        count={Math.ceil(searchResult.count/SEARCHSCREEN_PAGE_SIZE)}
                        page={currentPage}
                        shape='rounded'
                        onChange={onChangePage}
                        size='large'
                    />
                </Container>
            </>
            }
        </Container>
    );
}
CourseGroupSearchResultSection.propTypes = {
    error: PropType.object,
    searchResult: PropType.instanceOf(CourseGroupSearchResult),
    requestPage: PropType.func.isRequired,
    currentPage: PropType.number.isRequired,
    ordering: PropType.object.isRequired,
    onChangeOrdering: PropType.func.isRequired,
    hasQuery: PropType.bool.isRequired,
}